import React from 'react';
import type { IntlShape } from 'react-intl';
import { defineMessages } from 'react-intl';

import type { ExtensionManifest } from '@atlaskit/editor-common/extensions/extension-manifest';
import DefaultExtensionProvider from '@atlaskit/editor-common/extensions/default-extension-provider';
import type { EditorActions } from '@atlaskit/editor-core/actions';

import { expVal } from '@confluence/feature-experiments';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { LoadablePaint } from '@confluence/loadable';

type ProjectLinkerManifestArgs = {
	cloudId: string;
	intl: IntlShape;
	editorActions: EditorActions;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
};

export const projectLinkerExtensionProvider = async (args: ProjectLinkerManifestArgs) =>
	new DefaultExtensionProvider<{}>([getProjectLinkerExtensionManifest(args)]);

const getProjectLinkerExtensionManifest = ({
	cloudId,
	intl,
	editorActions,
	createAnalyticsEvent,
}: ProjectLinkerManifestArgs): ExtensionManifest<{}> => ({
	title: intl.formatMessage(
		expVal('project-terminology-refresh', 'isEnabled', false) ? i18n.titleGalaxia : i18n.title,
	),
	type: 'com.atlassian.confluence.project-linker',
	key: 'project-linker',
	description: intl.formatMessage(i18n.description),
	icons: {
		'48': async () => ({ default: () => null }),
	},
	modules: {
		nodes: {
			default: {
				type: 'extension',
				render: async () => () => (
					<ProjectLinkerLoader
						cloudId={cloudId}
						editorActions={editorActions}
						createAnalyticsEvent={createAnalyticsEvent}
					/>
				),
				// @ts-expect-error This option is experimental and not reflected in the manifest type definition
				__hideFrame: true,
			},
		},
	},
});

const ProjectLinkerLoader = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ProjectLinker" */ './ProjectLinker.tsx'))
			.ProjectLinkerWrapper,
});

const i18n = defineMessages({
	title: {
		id: 'create-space.project-linker.extension.title',
		defaultMessage: 'Project linker',
		description:
			'The text is shown as the title of the project linker ADF extension node in the Confluence editor, representing the block that lets users link a project to the space overview.',
	},
	titleGalaxia: {
		id: 'create-space.project-linker.extension.title-galaxia',
		defaultMessage: 'Space linker',
		description:
			'The text is shown as the title of the space linker ADF extension node in the Confluence editor when the project-terminology-refresh experiment is enabled, representing the block that lets users link a Jira space to the Confluence space overview.',
	},
	description: {
		id: 'create-space.project-linker.extension.description',
		defaultMessage: 'Provides a convenient way to link to projects on space overview',
		description: 'Description for project linker ADF extension',
	},
});
